import { uniq } from 'lodash';

import { ExamGridModel, FileModel } from 'models';

import { DocumentCategories } from 'features/exam/constants';

/**
 * Gets category name from category ID
 * @param categoryId - The category ID (e.g., 17 for AI Summary)
 * @returns The category name or 'Unknown' if not found
 */
export const getCategoryNameById = (categoryId: number | null): string => {
  if (categoryId === null) return 'Unknown';

  const category = Object.values(DocumentCategories).find((cat) => cat.value === categoryId);
  return category?.name || 'Unknown';
};

/**
 * Gets category key from category ID
 * @param categoryId - The category ID
 * @returns The category key (e.g., 'AISummary') or null if not found
 */
export const getCategoryKeyById = (categoryId: number | null): string | null => {
  if (categoryId === null) return null;

  const categoryEntry = Object.entries(DocumentCategories).find(([_, cat]) => cat.value === categoryId);

  return categoryEntry ? categoryEntry[0] : null;
};

/**
 * Checks if a file is an image based on its file type
 * @param fileType - The file type string
 * @returns Whether the file is an image
 */
export const isImageFile = (fileType: string | undefined | null): boolean => {
  if (!fileType) return false;

  const imageFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg', 'dicom', 'dcm'];
  return imageFileTypes.some((type) => fileType.toLowerCase().includes(type));
};

/**
 * Checks if a file is a PDF
 * @param fileType - The file type string
 * @returns Whether the file is a PDF
 */
export const isPdfFile = (fileType: string | undefined | null): boolean => {
  if (!fileType) return false;
  return fileType.toLowerCase().includes('pdf');
};

/**
 * Gets appropriate icon for file based on file type
 * @param fileType - The file type string
 * @returns The icon emoji to use
 */
export const getFileIcon = (fileType: string | undefined | null): string => {
  if (!fileType) return '📄';

  if (isPdfFile(fileType)) return '📑';
  if (isImageFile(fileType)) return '🖼️';
  if (fileType.toLowerCase().includes('dicom')) return '🔬';
  if (fileType.toLowerCase().includes('video')) return '🎬';

  // Special case for specific document types
  if (fileType.toLowerCase().includes('ekg')) return '💓';

  return '📄';
};

/**
 * Derives relevant tags from a service/exam description string.
 * @param description - The service/exam description string
 * @returns An array of identified tags (e.g., ['ct', 'lung'])
 */
export const getTagsForDescription = (description: string | null): string[] => {
  const tags: string[] = [];
  if (!description) return tags;

  const desc = description.toLowerCase();

  // General Exam Type
  if (desc.includes('exam')) tags.push('exam');

  // Modalities
  if (desc.includes('ct') || desc.includes('computed tomography')) tags.push('ct', 'image', 'exam');
  if (desc.includes('x-ray') || desc.includes('xray')) tags.push('x-ray', 'image', 'exam');
  if (desc.includes('ekg') || desc.includes('ecg') || desc.includes('electrocardiogram')) tags.push('ekg', 'ecg', 'cardiac', 'exam');
  if (desc.includes('pathology') || desc.includes('biopsy') || desc.includes('slide')) tags.push('pathology', 'biopsy', 'slide', 'exam');
  if (desc.includes('ultrasound') || desc.includes(' us ')) tags.push('ultrasound', 'us', 'image', 'exam');
  if (desc.includes('mri') || desc.includes('magnetic resonance')) tags.push('mri', 'image', 'exam');

  // Body Parts / Organs (add more as needed)
  if (desc.includes('chest') || desc.includes('thorax') || desc.includes('lung')) tags.push('lung');
  if (desc.includes('abdomen')) tags.push('abdomen');
  if (desc.includes('liver') || desc.includes('hepatic')) tags.push('liver');
  if (desc.includes('kidney') || desc.includes('renal')) tags.push('kidney');
  if (desc.includes('head') || desc.includes('brain')) tags.push('head');
  if (desc.includes('cardiac') || desc.includes('heart')) tags.push('cardiac');
  if (desc.includes('thyroid')) tags.push('thyroid');

  // Document Types / Concepts
  if (desc.includes('report')) tags.push('report', 'document');
  if (desc.includes('final')) tags.push('final report'); // Add specific tag if 'final' is present
  if (desc.includes('summary')) tags.push('summary', 'document');
  if (desc.includes('ai')) tags.push('ai');
  if (desc.includes('ventilator')) tags.push('ventilator', 'data');
  if (desc.includes('monitor') || desc.includes('vitals')) tags.push('monitoring', 'vitals', 'data');
  if (desc.includes('order') || desc.includes('requisition')) tags.push('order', 'document');
  if (desc.includes('cover')) tags.push('cover page', 'document');
  if (desc.includes('emr')) tags.push('emr', 'data', 'document');

  return uniq(tags); // Return unique tags
};

/**
 * Aggregates tags for a given file based on its category, associated exam, and file type.
 * @param file - The FileModel object
 * @param gridData - Array of ExamGridModel for context
 * @returns An array of unique, lowercase tags for the file
 */
export const getTagsForFile = (file: FileModel, gridData: ExamGridModel[]): string[] => {
  let tags: string[] = [];

  // 1. Tags from File Category Definition
  if (file.categoryId) {
    const categoryKey = getCategoryKeyById(file.categoryId);
    if (categoryKey) {
      const categoryInfo = DocumentCategories[categoryKey as keyof typeof DocumentCategories];
      if (categoryInfo?.tags) {
        tags = tags.concat(categoryInfo.tags);
      }
    }
  }

  // 2. Tags from linked Exam description and metadata
  if (file.examId) {
    const exam = gridData.find((e) => e.id === file.examId);
    if (exam) {
      const examDescription = ((exam.serviceDescription ?? '') + ' ' + (exam.description ?? '')).trim();
      if (examDescription) {
        tags = tags.concat(getTagsForDescription(examDescription));
      }
      // Convert organ/laterality to string before lowercasing
      if (exam.organ) tags.push(String(exam.organ).toLowerCase());
      if (exam.laterality) tags.push(String(exam.laterality).toLowerCase());
    }
  }

  // 3. Tags directly on the file (Placeholder for future)
  // if (file.tags && Array.isArray(file.tags)) {
  //     tags = tags.concat(file.tags);
  // }

  // 4. Tags based on file type
  if (isImageFile(file.fileType)) tags.push('image');
  if (isPdfFile(file.fileType)) tags.push('pdf', 'document');
  if (file.fileType?.toLowerCase().includes('dicom')) tags.push('dicom');

  // Ensure 'document' tag
  if (!tags.includes('document') && (isPdfFile(file.fileType) || file.categoryId)) {
    // Handle potential undefined categoryId
    const categoryKey = getCategoryKeyById(file.categoryId ?? null);
    const categoryName = categoryKey ? DocumentCategories[categoryKey as keyof typeof DocumentCategories]?.name : '';
    // Use RegExp.test()
    if (/Report|Order|Summary|Worksheet|Page|EMR/i.test(categoryName)) {
      tags.push('document');
    }
  }
  // Ensure 'exam' tag
  if (!tags.includes('exam') && file.categoryId) {
    // Handle potential undefined categoryId
    const categoryKey = getCategoryKeyById(file.categoryId ?? null);
    const categoryName = categoryKey ? DocumentCategories[categoryKey as keyof typeof DocumentCategories]?.name : '';
    // Use RegExp.test()
    if (/Exam|EKG|Pathology|X-Ray|CT/i.test(categoryName)) {
      tags.push('exam');
    }
  }

  return uniq(tags.map((tag) => tag.toLowerCase().trim()).filter((tag) => !!tag)); // Return unique, lowercase, non-empty tags
};

/**
 * Sorts files by date (newest first)
 * @param files - Array of FileModel objects
 * @returns Sorted array of files
 */
export const sortFilesByDate = (files: FileModel[]): FileModel[] => {
  return [...files].sort((a, b) => {
    const dateA = a.created ? new Date(a.created).valueOf() : 0;
    const dateB = b.created ? new Date(b.created).valueOf() : 0;
    return dateB - dateA;
  });
};

// Category colors mapping
export const CATEGORY_COLORS: Record<string, string> = {
  EXAM: '#4CAF50',
  COVER_PAGE: '#03A9F4',
  FINAL_REPORT: '#FF9800',
  KEY_IMAGE: '#E91E63',
  ORDER: '#673AB7',
  DATA: '#4CAF50',
  DOCUMENT: '#673AB7',
  EXAMAI: '#4CAF50',
  Worksheet: '#03A9F4',
  Ventilator: '#E91E63',
  Monitoring: '#673AB7',
  EKG: '#FF9800',
  Pathology: '#4CAF50',
  Link: '#03A9F4',
  ChestXRay: '#4CAF50',
  CT: '#03A9F4',
  AISummary: '#8E44AD',
  EMR: '#009688',
};

/**
 * Categorizes a service description into the appropriate category
 * @param serviceDescription - The service description
 * @returns The matching category key
 */
export const categorizeByDescription = (serviceDescription: string | null): string | null => {
  if (!serviceDescription) return null;

  const desc = serviceDescription.toLowerCase();
  if (desc.includes('ct') || desc.includes('computed tomography')) {
    return 'CT';
  }

  if (desc.includes('chest')) {
    return 'ChestXRay';
  }

  if (desc.includes('ekg') || desc.includes('ecg') || desc.includes('electrocardiogram')) {
    return 'EKG';
  }

  if (desc.includes('pathology') || desc.includes('biopsy')) {
    return 'Pathology';
  }

  if (desc.includes('report') || desc.includes('final')) {
    return 'FINAL_REPORT';
  }

  if (desc.includes('ventilator')) {
    return 'Ventilator';
  }

  if (desc.includes('monitor')) {
    return 'Monitoring';
  }

  if (desc.includes('ai') || desc.includes('summary')) {
    return 'AISummary';
  }

  return null;
};
